
import React, { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';

export interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  fixedWidth?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Sélectionnez des options...",
  className,
  fixedWidth = false
}) => {
  const [open, setOpen] = useState(false);

  // Log de debugging
  console.log('🔍 [MULTI-SELECT DEBUG]', {
    optionsCount: options.length,
    selectedCount: value.length,
    isOpen: open,
    options: options.map(o => o.label)
  });

  const handleUnselect = (item: string) => {
    console.log('🔄 [MULTI-SELECT] Unselecting:', item);
    if (item === "Aucun") {
      onChange([]);
    } else {
      onChange(value.filter((i) => i !== item));
    }
  };

  const handleSelect = (item: string) => {
    console.log('🔄 [MULTI-SELECT] Selecting:', item);
    if (item === "Aucun") {
      onChange(["Aucun"]);
    } else {
      // Remove "Aucun" if present and add the new item
      const newValue = value.filter(v => v !== "Aucun");
      if (value.includes(item)) {
        onChange(newValue.filter((i) => i !== item));
      } else {
        onChange([...newValue, item]);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between border-2 border-gray-300 focus:border-[#D7398B] rounded-full min-h-[40px] h-auto text-sm md:text-base", className)}
          onClick={() => {
            console.log('🔄 [MULTI-SELECT] Trigger clicked, setting open to:', !open);
            setOpen(!open);
          }}
        >
          <div className="flex gap-1 flex-wrap max-w-[calc(100%-24px)]">
            {value.length > 0 ? (
              value.map((item) => (
                <Badge
                  key={item}
                  className="mr-1 mb-1 bg-[#D7398B] text-white border-none hover:bg-[#D7398B]/80 text-xs max-w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUnselect(item);
                  }}
                >
                  <span className="truncate max-w-[120px]">{item}</span>
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className="ml-1 h-auto p-0 text-white hover:text-white/80"
                  >
                    <X className="h-3 w-3 flex-shrink-0" />
                  </Button>
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground text-sm">{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="p-0 bg-white border-2 border-gray-200 rounded-3xl z-[10100] shadow-lg"
        align="start"
        side="bottom"
        sideOffset={8}
        avoidCollisions={true}
        collisionPadding={20}
        style={{ 
          width: 'var(--radix-popover-trigger-width)',
          zIndex: 10100,
          position: 'fixed'
        }}
      >
        <div className="max-h-60 overflow-y-auto p-2">
          {options.map((option) => {
            console.log('🔍 [MULTI-SELECT] Rendering option:', option.label);
            return (
              <div
                key={option.value}
                onClick={() => {
                  console.log('🔄 [MULTI-SELECT] Option clicked:', option.label);
                  handleSelect(option.value);
                }}
                className={cn(
                  "flex items-center space-x-2 rounded-xl px-3 py-2 cursor-pointer hover:bg-[#D7398B]/10 break-words",
                  value.includes(option.value) && "bg-[#D7398B]/10"
                )}
              >
                <div className={cn(
                  "w-4 h-4 border-2 border-gray-300 rounded flex items-center justify-center flex-shrink-0",
                  value.includes(option.value) && "border-[#D7398B] bg-[#D7398B]"
                )}>
                  {value.includes(option.value) && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </div>
                <span className="text-sm break-words">{option.label}</span>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
